<template>
  <el-form class="backtest-form">
    <!-- Timezone -->
    <el-form-item label="Timezone">
      <el-select
        class="utc-select"
        v-model="form.offset"
        size="small"
        :placeholder="$t('text.please_select')"
        filterable
      >
        <el-option
          v-for="item in utcOffsetOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        >
        </el-option>
      </el-select>
    </el-form-item>

    <!-- Selection Mode Toggle -->
    <el-form-item label="Selection Mode">
      <el-radio-group
        v-model="form.selectionMode"
        @change="handleSelectionModeChange"
      >
        <el-radio label="device">Device(s)</el-radio>
        <el-radio label="patient">Patient(s)</el-radio>
      </el-radio-group>
    </el-form-item>

    <!-- Device ID(s) - Multi Select -->
    <el-form-item v-if="form.selectionMode === 'device'" label="Device ID">
      <el-select
        v-model="form.deviceIds"
        multiple
        placeholder="Select devices"
        filterable
        @change="onDeviceChange"
      >
        <el-option
          v-for="pod in deviceOptions"
          :key="pod.podId"
          :label="pod.podId"
          :value="pod.podId"
        >
        </el-option>
      </el-select>
    </el-form-item>

    <!-- Patient ID(s) - Multi Select -->
    <el-form-item v-if="form.selectionMode === 'patient'" label="Patient ID">
      <el-select
        v-model="form.patientIds"
        multiple
        placeholder="Select patients"
        filterable
        @change="onPatientChange"
      >
        <el-option
          v-for="patient in patientOptions"
          :key="patient.PatientId"
          :label="patient.Alias"
          :value="patient.PatientId"
        >
        </el-option>
      </el-select>
    </el-form-item>

    <!-- Time Period -->
    <el-form-item label="Time Period" class="date-field">
      <el-date-picker
        v-model="form.dateRange"
        type="datetimerange"
        range-separator="-"
        start-placeholder="Start date"
        end-placeholder="End date"
        value-format="yyyy-MM-dd HH:mm:ss"
      >
      </el-date-picker>
    </el-form-item>

    <!-- Algorithm Type -->
    <el-form-item label="Algorithm Type">
      <el-select v-model="form.algorithmType" placeholder="Select algorithm">
        <el-option
          v-for="option in algorithmTypeOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        />
      </el-select>
    </el-form-item>

    <!-- Start Test Button -->
    <el-button
      @click="createTest"
      :disabled="!isFormValid || creating"
      :loading="creating"
      type="primary"
      class="start-test-btn"
    >
      {{ creating ? 'CREATING...' : 'START TEST' }}
    </el-button>
  </el-form>
</template>

<script>
import moment from 'moment-timezone'
import { utcOffsets } from '@/const'
import BackTestApiService from '@/services/BackTestApiService'
import swal from 'sweetalert'
import { jobTypes } from '@/const'

export default {
  name: 'BackTestForm',
  data() {
    return {
      form: {
        offset: this.$store.state.pods.selectedUtcOffset,
        selectionMode: 'device',
        deviceIds: [],
        patientIds: [],
        dateRange: [],
        algorithmType: 'newpad-v1.0'
      },
      creating: false,
      utcOffsetOptions: utcOffsets,
      algorithmTypeOptions: [],
      deviceOptions: this.$store.state.pods.pods,
      patientOptions: []
    }
  },
  mounted() {
    this.fetchAlgorithmOptions()
    this.fetchDevices()
    this.fetchPatients()
  },
  computed: {
    isFormValid() {
      const hasSelection =
        this.form.selectionMode === 'device'
          ? this.form.deviceIds.length > 0
          : this.form.patientIds.length > 0

      return (
        hasSelection &&
        Array.isArray(this.form.dateRange) &&
        this.form.dateRange.length === 2 &&
        this.form.dateRange[0] &&
        this.form.dateRange[1] &&
        this.form.algorithmType
      )
    }
  },
  methods: {
    async fetchDevices() {
      await this.$store.dispatch('pods/fetchPods')
      this.deviceOptions = this.$store.state.pods.pods
    },
    async fetchPatients() {
      const response = await BackTestApiService.GetAllPatientsForConfig()
      this.patientOptions = response.data.data
    },
    async fetchAlgorithmOptions() {
      const response = await BackTestApiService.getAlgorithmOptions()
      this.algorithmTypeOptions = response.data.map(algo => ({
        label: algo,
        value: algo
      }))
    },
    handleSelectionModeChange(mode) {
      // Clear the other selection when mode changes
      if (mode === 'device') {
        this.form.patientIds = []
      } else {
        this.form.deviceIds = []
      }
    },
    onDeviceChange() {
      // Auto-clear patients if in device mode
      this.form.patientIds = []
    },
    onPatientChange() {
      // Auto-clear devices if in patient mode
      this.form.deviceIds = []
    },
    async createTest() {
      if (!this.isFormValid) return

      this.creating = true
      try {
        const isNewPad = this.form.algorithmType.indexOf('newpad') !== -1
        const isVolumeEstimation =
          this.form.algorithmType.indexOf('volumeestimation') !== -1

        let jobType = jobTypes.backTestingRedAlert // default to red alert
        if (isNewPad) {
          jobType = jobTypes.backTestingNewPad
        } else if (isVolumeEstimation) {
          jobType = jobTypes.backTestingVolumeEstimation
        }

        const parameters = {
          startTime: this.form.dateRange[0],
          endTime: this.form.dateRange[1],
          offset: moment.tz(this.form.offset).utcOffset() / 60,
          algorithmType: this.form.algorithmType,
          deviceIds:
            this.form.selectionMode === 'device' ? this.form.deviceIds : [],
          patientIds:
            this.form.selectionMode === 'patient' ? this.form.patientIds : []
        }

        const response = await BackTestApiService.createBackTest({
          jobType: jobType,
          parameters: parameters
        })

        swal({
          icon: 'success',
          title: 'Success',
          text: 'Test created successfully'
        })

        this.$emit('test-created', response.data.jobId)
      } catch (error) {
        swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to create test'
        })
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.backtest-form {
  ::v-deep .el-form-item {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
  }

  ::v-deep .el-form-item__label {
    width: 100px;
    line-height: 32px;
    text-align: left;
    padding: 0 12px 0 0;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  ::v-deep .el-form-item__content {
    flex: 1;
  }

  ::v-deep .el-select,
  ::v-deep .el-input,
  ::v-deep .el-date-editor {
    width: 100%;
  }

  .start-test-btn {
    align-self: flex-start;
    width: 100%;
    margin-top: 10px;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.5px;
  }
}
</style>
